import { rankItem, rankings } from "@tanstack/match-sorter-utils";
import { type ColumnDef, type FilterFn, type SortingState } from "@tanstack/react-table";
import { useCallback, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { z } from "zod";

// biome-ignore lint/suspicious/noExplicitAny: legacy any
export const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
	const itemRank = rankItem(row.getValue(columnId), value, {
		threshold: rankings.CONTAINS,
		keepDiacritics: true,
	});

	addMeta({ itemRank });
	return itemRank.passed;
};

export const getInitialColumnSizing = <TData, TValue>(
	columnDefs: ColumnDef<TData, TValue>[],
	locallyStoredValues?: Record<string, number>,
) => {
	const initialColumnSizing: Record<string, number> = {};

	columnDefs.forEach((columnDef) => {
		if (columnDef.id) {
			if (locallyStoredValues?.[columnDef.id]) {
				const columnMinsize = columnDef.minSize;
				const size = columnMinsize
					? Math.max(locallyStoredValues[columnDef.id], columnMinsize)
					: locallyStoredValues[columnDef.id];
				initialColumnSizing[columnDef.id] = size;
			} else if (columnDef.size !== undefined) {
				initialColumnSizing[columnDef.id] = columnDef.size;
			} else {
				initialColumnSizing[columnDef.id] = 180;
			}
		}
	});

	return initialColumnSizing;
};

export function useTableColumnSizing<TData, TValue>(
	storageKey: string,
	columnDefs: ColumnDef<TData, TValue>[],
) {
	const [columnSizing, setColumnSizing] = useState<Record<string, number>>(() => {
		try {
			const stored = localStorage.getItem(storageKey);
			const parsed = stored
				? z.record(z.string(), z.number()).safeParse(JSON.parse(stored))
				: undefined;
			const storedValues = parsed?.success ? parsed.data : undefined;
			return getInitialColumnSizing(columnDefs, storedValues);
		} catch {
			return getInitialColumnSizing(columnDefs);
		}
	});

	const debouncedPersist = useDebouncedCallback((sizing: Record<string, number>) => {
		localStorage.setItem(storageKey, JSON.stringify(sizing));
	}, 300);

	const handleColumnSizingChange = useCallback(
		(
			updater: Record<string, number> | ((old: Record<string, number>) => Record<string, number>),
		) => {
			setColumnSizing((prev) => {
				const next = typeof updater === "function" ? updater(prev) : updater;
				debouncedPersist(next);
				return next;
			});
		},
		[debouncedPersist],
	);

	return { columnSizing, handleColumnSizingChange };
}

export function useTableSorting(storageKey: string, initialSorting: SortingState = []) {
	const [sorting, setSorting] = useState<SortingState>(() => {
		try {
			const stored = sessionStorage.getItem(storageKey);
			if (!stored) return initialSorting;
			const result = z
				.array(z.object({ id: z.string(), desc: z.boolean() }))
				.safeParse(JSON.parse(stored));
			return result.success ? result.data : initialSorting;
		} catch {
			return [];
		}
	});

	const handleSortingChange = useCallback(
		(updater: SortingState | ((old: SortingState) => SortingState)) => {
			setSorting((prev) => {
				const next = typeof updater === "function" ? updater(prev) : updater;
				sessionStorage.setItem(storageKey, JSON.stringify(next));
				return next;
			});
		},
		[storageKey],
	);

	return { sorting, handleSortingChange };
}
